import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDoctorContext } from "../../../Navigation/context/DoctorContext";
import { useToast } from "../../../Common/hooks/useToast";
import { URLS } from "../../../Common/axios_instance/axios_instance";
import { getDynamicDocumentToken } from "../../../Common/axios_instance/azure_https/azure.https";
import { uploadAndSaveProfilePicture } from "../https/profile_module.https";
import { ProfileModuleHookReturn } from "../interfaces/profile_module.interface";

const ALLOWED_TYPES = ["image/jpeg", "image/png"];
const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

export const useProfileModule = (): ProfileModuleHookReturn => {
  const navigate = useNavigate();
  const { doctorProfile, isDoctorProfileLoading, refreshDoctorProfile } = useDoctorContext();
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [resolvedPhotoUrl, setResolvedPhotoUrl] = useState<string | null>(null);

  // Resolve profile photo URL via token when profile loads
  useEffect(() => {
    if (!doctorProfile?.profile_photo) return;
    const cleanPath = doctorProfile.profile_photo.startsWith("/")
      ? doctorProfile.profile_photo.slice(1)
      : doctorProfile.profile_photo;
    const parts = cleanPath.split("/");
    const docName = parts.pop() || "";
    if (cleanPath.startsWith("doctor-documents")) {
      const docFolder = parts.join("/");
      getDynamicDocumentToken({ docFolder, docName })
        .then((res: any) => {
          const token = res.data?.data?.token;
          if (token) {
            setResolvedPhotoUrl(`${URLS.AZURE_URI}/signedUrl/view-document?token=${encodeURIComponent(token)}`);
          }
        })
        .catch(() => {/* photo just won't load — non-critical */});
    }
  }, [doctorProfile?.profile_photo]);

  useEffect(() => {
    if (isDoctorProfileLoading) return;
    const doctorId = localStorage.getItem("doctorId");
    const doctorRole = localStorage.getItem("doctorRole");
    const isLoggedIn = !!doctorId && doctorRole === "DOCTOR";
    if (!isLoggedIn) {
      navigate("/");
    }
  }, [isDoctorProfileLoading, navigate]);

  const name = doctorProfile?.name || "";
  const cleanName = name.replace(/^(dr\.?\s*)/i, "");
  const displayName = name.toLowerCase().startsWith("dr.") ? name : `Dr. ${name}`;
  const initials = cleanName
    ? cleanName.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2)
    : "DR";

  const getProfilePhotoUrl = (profilePhoto: string): string => {
    // For doctor-documents, use the pre-resolved token URL if available
    const cleanPath = profilePhoto.startsWith("/") ? profilePhoto.slice(1) : profilePhoto;
    if (cleanPath.startsWith("doctor-documents")) {
      return resolvedPhotoUrl || "";
    }
    // For beneficiary documents, use the signed URL directly
    const parts = cleanPath.split("/");
    const docName = parts.pop() || "";
    const benId = parts[0] || "";
    const docFolder = parts.slice(1).join("/");
    return `${URLS.AZURE_URI}/signedUrl/get-document/${benId}/${docFolder}/${docName}`;
  };

  const handleLogout = () => {
    localStorage.removeItem("doctorId");
    localStorage.removeItem("doctorName");
    localStorage.removeItem("doctorRole");
    localStorage.removeItem("read_notification_ids");
    sessionStorage.clear();
    showToast("Logged out successfully", "success");
    setTimeout(() => navigate("/"), 1000);
  };

  const handleAvatarClick = () => {
    if (!isUploading) fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !doctorProfile?._id) return;
    e.target.value = "";

    if (!ALLOWED_TYPES.includes(file.type)) {
      showToast("Only JPG and PNG images are allowed", "error");
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      showToast("Image must be less than 5MB", "error");
      return;
    }

    setIsUploading(true);
    try {
      await uploadAndSaveProfilePicture(file, doctorProfile._id, refreshDoctorProfile);
      showToast("Profile picture updated!", "success");
    } catch (err: any) {
      const errMsg = err.response?.data?.message || err.message || "Failed to upload profile picture";
      showToast(Array.isArray(errMsg) ? errMsg.join(", ") : String(errMsg), "error");
    } finally {
      setIsUploading(false);
    }
  };

  return {
    isUploading,
    fileInputRef,
    displayName,
    initials,
    resolvedPhotoUrl,
    getProfilePhotoUrl,
    handleLogout,
    handleAvatarClick,
    handleFileChange,
  };
};
